import { LayoutDashboard, Users, BadgeDollarSign, FileText, CheckSquare, FolderKanban, Receipt, FileCheck, Wallet, Clock } from 'lucide-react'

// The module/action toggle grid, extracted from UserManageModal's "Module
// Access" tab so the same UI can be reused by the permission-template admin
// panel — one editable Switch per (module, action) pair — instead of
// building a second implementation.

export const MODULES = [
  { key: 'DASHBOARD', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'LEADS', label: 'Leads', icon: Users },
  { key: 'DEALS', label: 'Deals', icon: BadgeDollarSign },
  // SALES_DOCS gates the shared quotation→proforma→invoice lifecycle actions
  // (submit, request-proforma, etc.) and attachments. The three
  // below split out which document TYPE a user can originate or reach the
  // standalone list page for — a Sales Rep gets Quotations only.
  { key: 'SALES_DOCS', label: 'Sales Docs (lifecycle actions)', icon: FileText },
  { key: 'QUOTATIONS', label: 'Quotations', icon: FileText },
  { key: 'PROFORMA_INVOICES', label: 'Proforma Invoices', icon: FileCheck },
  { key: 'INVOICE_REQUESTS', label: 'Invoice Requests', icon: Receipt },
  // Previously had no toggle at all in this grid despite being a real,
  // enforced permission module (backend-gated Accounts approval actions,
  // Milestone Payments) — could only ever be granted via direct DB access.
  { key: 'ACCOUNTS', label: 'Accounts', icon: Wallet },
  { key: 'TASKS', label: 'Tasks', icon: CheckSquare },
  { key: 'SPACES', label: 'Spaces / Projects', icon: FolderKanban },
  { key: 'PEOPLE', label: 'People / Users', icon: Users },
  { key: 'TIMESHEETS', label: 'Timesheets', icon: Clock },
]

export const ACTIONS = ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'ASSIGN']

export function Switch({
  checked,
  onCheckedChange,
  disabled,
}: {
  checked: boolean
  onCheckedChange: (c: boolean) => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onCheckedChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${
        checked ? 'bg-[#6E11E3]' : 'bg-gray-300 dark:bg-gray-700'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  )
}

export interface PermissionEntry {
  module: string
  actions: string[]
}

export function hasModuleAction(permissions: PermissionEntry[], module: string, action: string): boolean {
  return permissions.some(
    (p) =>
      (p.module === module ||
        (module === 'PEOPLE' && p.module === 'USERS') ||
        (module === 'USERS' && p.module === 'PEOPLE')) &&
      (p.actions.includes(action) || p.actions.includes('*'))
  )
}

export function togglePermissionEntry(
  permissions: PermissionEntry[],
  module: string,
  action: string
): PermissionEntry[] {
  const existing = permissions.find((p) => p.module === module)
  if (existing) {
    if (existing.actions.includes(action)) {
      const newActions = existing.actions.filter((a) => a !== action)
      if (newActions.length === 0) {
        return permissions.filter((p) => p.module !== module)
      }
      return permissions.map((p) => (p.module === module ? { ...p, actions: newActions } : p))
    }
    return permissions.map((p) => (p.module === module ? { ...p, actions: [...p.actions, action] } : p))
  }
  return [...permissions, { module, actions: [action] }]
}

export function toggleAllModuleActions(
  permissions: PermissionEntry[],
  module: string,
  enable: boolean
): PermissionEntry[] {
  if (enable) {
    const existing = permissions.find((p) => p.module === module)
    const currentActions = existing ? existing.actions : []
    const newActions = ACTIONS.filter((a) => !currentActions.includes(a))
    if (newActions.length === 0) return permissions
    if (existing) {
      return permissions.map((p) =>
        p.module === module ? { ...p, actions: [...p.actions, ...newActions] } : p
      )
    }
    return [...permissions, { module, actions: ACTIONS }]
  }
  return permissions.filter((p) => p.module !== module)
}

export function PermissionToggleGrid({
  permissions,
  onToggle,
  onToggleAll,
  disabled,
}: {
  permissions: PermissionEntry[]
  onToggle: (module: string, action: string) => void
  onToggleAll?: (module: string, enable: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className="space-y-3">
      {MODULES.map((mod) => {
        const allChecked = ACTIONS.every((action) => hasModuleAction(permissions, mod.key, action))
        const noneChecked = ACTIONS.every((action) => !hasModuleAction(permissions, mod.key, action))
        return (
          <div
            key={mod.key}
            className="flex flex-col sm:flex-row sm:items-center justify-between rounded-lg border border-border p-3 gap-2"
          >
            <div className="flex items-center gap-2">
              <mod.icon className="h-4 w-4 text-[#6E11E3]" />
              <span className="text-sm font-semibold">{mod.label}</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {onToggleAll && (
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={disabled || allChecked}
                    onClick={() => onToggleAll(mod.key, true)}
                    className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                      allChecked
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600 dark:border-gray-700'
                        : 'bg-[#6E11E3] text-white border-[#6E11E3] hover:bg-[#5B13B8] cursor-pointer'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    disabled={disabled || noneChecked}
                    onClick={() => onToggleAll(mod.key, false)}
                    className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                      noneChecked
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600 dark:border-gray-700'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 cursor-pointer dark:bg-gray-900 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-800'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    None
                  </button>
                </div>
              )}
              {ACTIONS.map((action) => {
                const checked = hasModuleAction(permissions, mod.key, action)
                return (
                  <label key={action} className="flex items-center gap-1.5 cursor-pointer text-xs">
                    <Switch
                      checked={checked}
                      onCheckedChange={() => onToggle(mod.key, action)}
                      disabled={disabled}
                    />
                    <span className="text-muted-foreground">{action}</span>
                  </label>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
